/**
 * Living things you can put down, and where they will stand.
 *
 * The комора hands out blocks and tools; a creature is neither. So each one
 * gets an item that is not the creature but a way of letting one out - put it
 * down and the animal is there.
 *
 * **Only for the режим творця.** None of these is craftable and none is
 * generated anywhere. They exist so the world can be filled by hand, which is
 * what the storeroom is for.
 *
 * The table is data rather than a switch, so adding a creature is a row here
 * and an icon - but the *making* of one stays in main.ts, because a pig, a
 * fish, an orc and a villager are four unrelated types kept in four different
 * lists, and pretending otherwise would need a common entity interface this
 * game has deliberately never grown.
 */

import type { Lang } from "../text/lang.js";
import { PlainItemId } from "../block/items.js";

/** Which of them it is. Matched in main.ts against the right constructor. */
export type Spawnable =
  | "pig" | "tur" | "kurka" | "sobaka" | "kit"
  | "karas" | "orc" | "selianyn" | "selianka";

export interface SpawnDef {
  readonly kind: Spawnable;
  readonly item: number;
  readonly label: string;
  readonly en: string;
  /**
   * Where it can be put.
   *
   * A карась dropped on a hillside would flop and die, and a свиня dropped in
   * the sea would drown - both of which read as the storeroom being broken
   * rather than as the player having made a mistake. So the item refuses, and
   * says which it wanted.
   */
  readonly habitat: "land" | "water";
  /** Inventory icon. */
  readonly tile: number;
}

export const SPAWNS: readonly SpawnDef[] = [
  {
    kind: "pig", item: PlainItemId.SpawnPig,
    label: "свиня", en: "pig", habitat: "land", tile: 130,
  },
  {
    kind: "tur", item: PlainItemId.SpawnTur,
    label: "тур", en: "aurochs", habitat: "land", tile: 131,
  },
  {
    kind: "kurka", item: PlainItemId.SpawnKurka,
    label: "курка", en: "hen", habitat: "land", tile: 153,
  },
  {
    kind: "sobaka", item: PlainItemId.SpawnSobaka,
    label: "собака", en: "dog", habitat: "land", tile: 156,
  },
  {
    kind: "kit", item: PlainItemId.SpawnKit,
    label: "кіт", en: "cat", habitat: "land", tile: 157,
  },
  {
    // «Живий», because there is a карась you eat as well: two things of the
    // same name in one bag is a bag nobody can read.
    kind: "karas", item: PlainItemId.SpawnKaras,
    label: "живий карась", en: "live crucian", habitat: "water", tile: 132,
  },
  {
    kind: "orc", item: PlainItemId.SpawnOrc,
    label: "орк", en: "orc", habitat: "land", tile: 133,
  },
  {
    kind: "selianyn", item: PlainItemId.SpawnSelianyn,
    label: "селянин", en: "villager man", habitat: "land", tile: 134,
  },
  {
    kind: "selianka", item: PlainItemId.SpawnSelianka,
    label: "селянка", en: "villager woman", habitat: "land", tile: 135,
  },
];

export function spawnForItem(item: number): SpawnDef | undefined {
  return SPAWNS.find((s) => s.item === item);
}

export function spawnLabel(def: SpawnDef, lang: Lang): string {
  return lang === "en" ? def.en : def.label;
}
